import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, MapPin, Clock, Wrench, ChevronDown, ChevronUp, ArrowLeft, Star, Shield, Zap, Search, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = { zap: Zap, shield: Shield, wrench: Wrench, star: Star, clock: Clock, phone: Phone };
const COLOR_MAP = {
  amber: 'text-amber-400 bg-amber-500/10',
  blue: 'text-blue-400 bg-blue-500/10',
  green: 'text-green-400 bg-green-500/10',
  purple: 'text-purple-400 bg-purple-500/10',
  red: 'text-red-400 bg-red-500/10',
};

const TarifsPage = () => {
  const navigate = useNavigate();
  const { t, isRtl, formatCurrency } = useLanguage();
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedBrand, setExpandedBrand] = useState(null);
  const [search, setSearch] = useState('');
  const [settings, setSettings] = useState({});
  const [pc, setPc] = useState(null); // page content

  useEffect(() => {
    axios.get(`${API}/api/public/service-prices`).then(r => {
      setPrices(r.data || []);
      if (r.data?.length > 0) {
        const brands = [...new Set(r.data.map(p => p.marque))];
        if (brands.length > 0) setExpandedBrand(brands[0]);
      }
    }).catch(() => {}).finally(() => setLoading(false));
    axios.get(`${API}/api/public/site-settings`).then(r => setSettings(r.data || {})).catch(() => {});
    axios.get(`${API}/api/public/page-content/tarifs`).then(r => setPc(r.data || null)).catch(() => {});
  }, []);

  const accent = settings.accent_color || '#7c3aed';
  const c = pc || {}; // shorthand

  const filtered = search
    ? prices.filter(p =>
        (p.marque + ' ' + p.modele + ' ' + p.type_reparation).toLowerCase().includes(search.toLowerCase())
      )
    : prices;

  const grouped = filtered.reduce((acc, p) => {
    const key = p.marque || 'Autre';
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  // Badges / FAQ : contenu éditable côté admin (backend). Fallbacks FR conservés ici ;
  // leur traduction complète relève de la passe « contenu » (à relire par Carlos).
  const badges = c.badges || [
    { label: 'Reparation Express', desc: 'En 30 min pour la plupart', icon: 'zap', color: 'amber' },
    { label: 'Garantie 6 mois', desc: 'Sur toutes nos reparations', icon: 'shield', color: 'blue' },
    { label: 'Pieces certifiees', desc: 'Qualite premium', icon: 'wrench', color: 'green' },
    { label: 'Devis gratuit', desc: 'Sans engagement', icon: 'star', color: 'purple' },
  ];

  const faq = c.faq || [];

  const phoneNum = c.phone || '0694274051';
  const phoneDisplay = c.phone_display || '0694 27 40 51';
  const address = c.address || '102 Avenue General de Gaulle, Porte 2, 97300 Cayenne';
  const hours = c.hours || 'Lun-Ven 9h-19h, Sam 9h-13h';
  const whatsapp = c.whatsapp || '594694274051';
  const pageTitle = c.title || t('tarifs.titleFallback');
  const titleAccent = c.title_accent || 'Cayenne'; // nom propre — non traduit
  const subtitle = c.subtitle || t('tarifs.subtitleFallback');
  const ctaTitle = c.cta_title || t('tarifs.ctaTitleFallback');
  const ctaDesc = c.cta_description || t('tarifs.ctaDescFallback');
  const footerLine1 = c.footer_line1 || 'Resurrection Mobile - Reparation telephone Cayenne, Guyane Francaise';
  const footerLine2 = c.footer_line2 || '102 Avenue General de Gaulle, Porte 2, 97300 Cayenne | 0694 27 40 51';

  const priceLabel = (p) => (p.prix_min === p.prix_max
    ? formatCurrency(p.prix_min)
    : `${formatCurrency(p.prix_min)} – ${formatCurrency(p.prix_max)}`);

  return (
    <div dir={isRtl ? 'rtl' : 'ltr'} className="min-h-screen bg-gray-950 text-white">
      {/* SEO Header */}
      <header className="border-b border-white/5" style={{ background: 'linear-gradient(180deg, rgba(124,58,237,0.06) 0%, transparent 100%)' }}>
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors" data-testid="back-home">
              <ArrowLeft className="w-4 h-4" /> {t('tarifs.back')}
            </button>
            <a href={`tel:${phoneNum}`} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-500/10 text-green-400 text-xs font-semibold hover:bg-green-500/20 transition-colors">
              <Phone className="w-3.5 h-3.5" /> {phoneDisplay}
            </a>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white mb-2" data-testid="tarifs-h1">
            {pageTitle} <span style={{ color: accent }}>{titleAccent}</span>
          </h1>
          <p className="text-base text-gray-400 max-w-2xl">{subtitle}</p>
          <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-gray-500">
            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" style={{ color: accent }} /> {address}</span>
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" style={{ color: accent }} /> {hours}</span>
          </div>
        </div>
      </header>

      {/* Trust badges */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {badges.map((b, i) => {
            const Icon = ICON_MAP[b.icon] || Star;
            const colorClass = COLOR_MAP[b.color] || COLOR_MAP.blue;
            return (
              <div key={i} className="p-4 rounded-xl border border-white/5 bg-white/[0.02] text-center">
                <div className={`w-10 h-10 rounded-xl ${colorClass} flex items-center justify-center mx-auto mb-2`}>
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-xs font-bold text-white">{b.label}</h3>
                <p className="text-[10px] text-gray-600 mt-0.5">{b.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Update notice */}
        {!loading && prices.length === 0 && (
          <div className="mb-6 p-4 rounded-xl border border-amber-500/20 bg-amber-500/5 flex items-center gap-3" data-testid="update-notice">
            <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-300">{t('tarifs.updateTitle')}</p>
              <p className="text-xs text-amber-400/60 mt-0.5">{t('tarifs.updateDesc')}</p>
            </div>
            <a href={`tel:${phoneNum}`} className="shrink-0 px-4 py-2 rounded-lg bg-amber-500/15 text-amber-300 text-xs font-semibold hover:bg-amber-500/25 transition-colors">
              {t('tarifs.call')}
            </a>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('tarifs.searchPlaceholder')}
            className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/[0.03] border border-white/10 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-white/20"
            data-testid="tarifs-search" />
        </div>

        {/* Pricing tables */}
        {loading ? (
          <div className="text-center py-16 text-gray-600">{t('tarifs.loading')}</div>
        ) : Object.keys(grouped).length === 0 ? (
          <div className="text-center py-16">
            <Wrench className="w-12 h-12 text-gray-800 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-gray-400 mb-2">
              {search ? t('tarifs.noResult') : t('tarifs.soon')}
            </h2>
            <p className="text-sm text-gray-600">
              {search ? t('tarifs.tryOther') : t('tarifs.contactFree')}
            </p>
            <a href={`tel:${phoneNum}`} className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90" style={{ background: accent }}>
              <Phone className="w-4 h-4" /> {t('tarifs.callNow')}
            </a>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([marque, items]) => (
              <div key={marque} className="rounded-2xl border border-white/5 overflow-hidden" data-testid={`public-brand-${marque}`}>
                <button onClick={() => setExpandedBrand(expandedBrand === marque ? null : marque)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.02] transition-colors"
                  data-testid={`brand-toggle-${marque}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-lg font-black" style={{ color: accent }}>
                      {marque.charAt(0)}
                    </div>
                    <div className="text-left">
                      <h2 className="text-base font-bold text-white">{t('tarifs.repairBrand', { brand: marque })}</h2>
                      <p className="text-[11px] text-gray-600">{t('tarifs.tariffsCount', { count: items.length })}</p>
                    </div>
                  </div>
                  {expandedBrand === marque ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
                </button>

                {expandedBrand === marque && (
                  <div className="border-t border-white/5">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[10px] uppercase text-gray-600 border-b border-white/5 bg-white/[0.01]">
                          <th className="px-5 py-2 text-left">{t('tarifs.colModel')}</th>
                          <th className="px-5 py-2 text-left">{t('tarifs.colRepair')}</th>
                          <th className="px-5 py-2 text-right">{t('tarifs.colPrice')}</th>
                          <th className="px-5 py-2 text-center hidden sm:table-cell">{t('tarifs.colDelay')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((p, i) => (
                          <tr key={p.id || i} className="border-b border-white/[0.02] hover:bg-white/[0.015] transition-colors">
                            <td className="px-5 py-3 text-white font-medium">{p.modele || t('tarifs.allModels')}</td>
                            <td className="px-5 py-3 text-gray-400">{p.type_reparation}</td>
                            <td className="px-5 py-3 text-right">
                              <span className="font-mono font-bold text-green-400">
                                {priceLabel(p)}
                              </span>
                            </td>
                            <td className="px-5 py-3 text-center text-gray-500 hidden sm:table-cell">{p.delai || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-12 mb-8 p-8 rounded-2xl border border-white/10 text-center" style={{ background: `linear-gradient(135deg, ${accent}08, transparent)` }}>
          <h2 className="text-xl font-bold text-white mb-2">{ctaTitle}</h2>
          <p className="text-sm text-gray-400 mb-6 max-w-lg mx-auto">{ctaDesc}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href={`tel:${phoneNum}`} className="px-6 py-3 rounded-xl text-sm font-semibold text-white flex items-center gap-2 hover:opacity-90 transition-all" style={{ background: accent }} data-testid="cta-call">
              <Phone className="w-4 h-4" /> {t('tarifs.callPrefix')}{phoneDisplay}
            </a>
            <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" className="px-6 py-3 rounded-xl text-sm font-semibold text-green-400 bg-green-500/10 border border-green-500/20 flex items-center gap-2 hover:bg-green-500/20 transition-all" data-testid="cta-whatsapp">
              <MessageCircle className="w-4 h-4" /> {t('tarifs.whatsapp')}
            </a>
            <button onClick={() => navigate('/login/client')} className="px-6 py-3 rounded-xl text-sm font-semibold text-gray-300 bg-white/5 border border-white/10 flex items-center gap-2 hover:bg-white/10 transition-all" data-testid="cta-portal">
              {t('tarifs.portal')}
            </button>
          </div>
        </div>

        {/* SEO FAQ */}
        {faq.length > 0 && (
          <div className="mb-12">
            <h2 className="text-lg font-bold text-white mb-4">{t('tarifs.faqTitle')}</h2>
            <div className="space-y-3">
              {faq.map((f, i) => (
                <div key={i} className="p-4 rounded-xl border border-white/5 bg-white/[0.01]" itemScope itemType="https://schema.org/Question" data-testid={`faq-${i}`}>
                  <h3 className="text-sm font-semibold text-white mb-1" itemProp="name">{f.question}</h3>
                  <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                    <p className="text-xs text-gray-400" itemProp="text">{f.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 text-center">
        <p className="text-xs text-gray-600">{footerLine1}</p>
        <p className="text-xs text-gray-700 mt-1">{footerLine2}</p>
      </footer>

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Resurrection Mobile",
        "alternateName": "R.E.Mobiles",
        "url": "https://remobiles.com",
        "telephone": `+${whatsapp}`,
        "email": "contact@remobiles.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "102 Avenue General de Gaulle, Porte 2",
          "addressLocality": "Cayenne",
          "postalCode": "97300",
          "addressRegion": "Guyane",
          "addressCountry": "FR"
        },
        "geo": { "@type": "GeoCoordinates", "latitude": 4.9333, "longitude": -52.3333 },
        "image": "https://remobiles.com/logo.png",
        "priceRange": "$$",
        "openingHoursSpecification": [
          { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "09:00", "closes": "19:00" },
          { "@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "09:00", "closes": "13:00" }
        ],
        "sameAs": ["https://www.google.com/maps/place/RESURRECTION+MOBILE"],
        "description": "Reparation de telephones et tablettes a Cayenne, Guyane Francaise. iPhone, Samsung, Huawei. Reparation express, garantie 6 mois."
      })}} />
    </div>
  );
};

export default TarifsPage;
